import * as React from "react"
import { Head, Link, router } from "@inertiajs/react"
import { Archive, Ghost, Search } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { FollowupDateControl } from "@/components/inbox/FollowupDateControl"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { cn } from "@/lib/utils"

type ClientOption = {
  readonly id: number
  readonly slug: string
  readonly display_name: string
  readonly color_hex: string
}

type PipelineCard = {
  readonly id: number
  readonly lead_name: string
  readonly lead_email: string
  readonly company: string
  readonly campaign_name: string
  readonly classification: string
  readonly stage: string
  readonly op_stage: string | null
  readonly op_stage_days: number | null
  readonly stage_age_days: number | null
  readonly last_contact_days: number | null
  readonly overdue_by_days: number
  readonly next_step_note: string | null
  readonly followup_count: number
  readonly next_followup_on: string | null
  readonly client: ClientOption | null
}

type PipelineColumn = {
  readonly key: string
  readonly label: string
  readonly sla_days: number | null
  readonly is_terminal: boolean
  readonly cards: readonly PipelineCard[]
}

type PipelineProps = {
  readonly columns: readonly PipelineColumn[]
  readonly clients: readonly ClientOption[]
  readonly filters: {
    readonly client_id: string | null
    readonly q: string
    readonly show_closed: boolean
    readonly show_ghosted: boolean
  }
  readonly closed_counts: Record<string, number>
  readonly ghosted_count: number
}

type PendingDrop = {
  readonly replyId: number
  readonly stage: string
}

export default function Pipeline({ columns, clients, filters, closed_counts, ghosted_count }: PipelineProps) {
  const description = "Move ReplyOps opportunities through the sales pipeline and dry-run won automations."
  const [query, setQuery] = React.useState(filters.q)
  const [pendingDrop, setPendingDrop] = React.useState<PendingDrop | null>(null)
  const [reason, setReason] = React.useState("")
  const closedTotal = Object.values(closed_counts).reduce((sum, count) => sum + count, 0)

  const submitFilters = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    router.get("/pipeline", {
      client_id: filters.client_id ?? undefined,
      q: query || undefined,
      show_closed: filters.show_closed ? "1" : undefined,
      show_ghosted: filters.show_ghosted ? "1" : undefined,
    }, { preserveState: true, preserveScroll: true })
  }

  const moveCard = (replyId: number, stage: string, dropReason = "") => {
    router.post(`/pipeline/${replyId}/stage`, { stage, reason: dropReason }, { preserveScroll: true })
  }

  const onDrop = (event: React.DragEvent<HTMLElement>, stage: string) => {
    event.preventDefault()
    const value = event.dataTransfer.getData("text/reply-id")
    const replyId = Number(value)
    if (!Number.isFinite(replyId)) return
    if (stage === "lost") {
      setPendingDrop({ replyId, stage })
      return
    }
    moveCard(replyId, stage)
  }

  return (
    <>
      <Head title="Pipeline">
        <meta name="description" content={description} />
        <meta property="og:title" content="Pipeline" />
        <meta property="og:description" content={description} />
      </Head>
      <AppShell wide>
        <div className="flex flex-col gap-6">
          <header>
            <h1>Pipeline</h1>
            <p className="mt-2">Prioritize opportunities by stage, SLA, and last outbound touch.</p>
          </header>

          <section className="rounded-lg border border-hairline bg-page p-4">
            <form onSubmit={submitFilters} className="grid gap-3 lg:grid-cols-[1fr_220px_auto_auto]">
              <label className="space-y-2">
                Search
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
                  <Input value={query} onChange={(event) => setQuery(event.target.value)} className="pl-9" placeholder="lead, company, subject, or reply text" />
                </div>
              </label>
              <label className="space-y-2">
                Client
                <Select
                  value={filters.client_id ?? ""}
                  onChange={(event) => router.get("/pipeline", {
                    client_id: event.target.value || undefined,
                    q: query || undefined,
                    show_closed: filters.show_closed ? "1" : undefined,
                    show_ghosted: filters.show_ghosted ? "1" : undefined,
                  })}
                >
                  <option value="">All clients</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>{client.display_name}</option>
                  ))}
                </Select>
              </label>
              <Button type="submit">Search</Button>
              <Button asChild variant="secondary">
                <Link href={pipelineLink(filters, { show_closed: !filters.show_closed })}>
                  <Archive className="h-4 w-4" />
                  Closed {closedTotal}
                </Link>
              </Button>
              <Button asChild variant="secondary">
                <Link href={pipelineLink(filters, { show_ghosted: !filters.show_ghosted })}>
                  <Ghost className="h-4 w-4" />
                  Ghosted {ghosted_count}
                </Link>
              </Button>
            </form>
          </section>

          <div className="flex gap-4 overflow-x-auto pb-3">
            {columns.map((column) => (
              <section
                key={column.key}
                className="min-h-[520px] w-[300px] shrink-0 rounded-lg border border-hairline bg-surface p-3"
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => onDrop(event, column.key)}
              >
                <header className="mb-3 flex items-center justify-between gap-2">
                  <div>
                    <h2>{column.label}</h2>
                    <p className="mt-1 text-sm text-ink-muted">
                      {column.sla_days ? `${column.sla_days}d SLA` : "No SLA"}
                    </p>
                  </div>
                  <Badge tone={column.is_terminal ? "muted" : "neutral"}>{column.cards.length}</Badge>
                </header>
                <div className="space-y-3">
                  {column.cards.map((card) => <KanbanCard key={card.id} card={card} />)}
                </div>
              </section>
            ))}
          </div>
        </div>
      </AppShell>

      <Dialog open={pendingDrop !== null} onOpenChange={(open) => !open && setPendingDrop(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reason</DialogTitle>
            <DialogDescription>Store the close reason on this pipeline card.</DialogDescription>
          </DialogHeader>
          <label htmlFor="stage_reason">Reason</label>
          <Input id="stage_reason" value={reason} onChange={(event) => setReason(event.target.value)} />
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => setPendingDrop(null)}>Cancel</Button>
            <Button
              type="button"
              onClick={() => {
                if (!pendingDrop) return
                moveCard(pendingDrop.replyId, pendingDrop.stage, reason)
                setReason("")
                setPendingDrop(null)
              }}
            >
              Move card
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function pipelineLink(filters: PipelineProps["filters"], changes: Partial<Pick<PipelineProps["filters"], "show_closed" | "show_ghosted">>) {
  const search = new URLSearchParams()
  const showClosed = changes.show_closed ?? filters.show_closed
  const showGhosted = changes.show_ghosted ?? filters.show_ghosted

  if (filters.client_id) search.set("client_id", filters.client_id)
  if (filters.q) search.set("q", filters.q)
  if (showClosed) search.set("show_closed", "1")
  if (showGhosted) search.set("show_ghosted", "1")

  const query = search.toString()
  return query ? `/pipeline?${query}` : "/pipeline"
}

function KanbanCard({ card }: { readonly card: PipelineCard }) {
  const opStageTone = card.op_stage && ["replied", "list_sent", "asset_sent"].includes(card.op_stage) && card.op_stage_days !== null && card.op_stage_days > 7
    ? "signal"
    : card.op_stage && ["won", "lost", "parked"].includes(card.op_stage)
      ? "muted"
      : "neutral"

  return (
    <article
      draggable
      onDragStart={(event) => event.dataTransfer.setData("text/reply-id", String(card.id))}
      className={cn(
        "cursor-grab rounded-lg border border-hairline bg-page p-3 active:cursor-grabbing",
        card.overdue_by_days > 0 && "border-danger bg-danger-faded",
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <h3 className="truncate">{card.lead_name}</h3>
        {card.overdue_by_days > 0 && <Badge tone="signal">{card.overdue_by_days}d late</Badge>}
      </div>
      <p className="mt-1 truncate text-sm text-ink-muted">{card.lead_email}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {card.client && <Badge tone="neutral">{card.client.display_name}</Badge>}
        {card.classification && <Badge tone="muted">{card.classification}</Badge>}
        {card.op_stage && <Badge tone={opStageTone}>{card.op_stage.split("_").join(" ")}{card.op_stage_days !== null && ` ${card.op_stage_days}d`}</Badge>}
        {card.next_followup_on && <Badge tone="accent">FU{card.followup_count} - next {card.next_followup_on}</Badge>}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-sm text-ink-muted">
        <span>Stage {card.stage_age_days ?? 0}d</span>
        <span>Touch {card.last_contact_days ?? "none"}</span>
      </div>
      <div className="mt-3">
        <FollowupDateControl replyId={card.id} nextFollowupOn={card.next_followup_on} id={`pipeline_followup_${card.id}`} />
      </div>
      <Link href={`/inbox?selected=${card.id}`} className="mt-3 inline-block text-sm">Open thread</Link>
    </article>
  )
}
